// Mesh-based vertical aurora curtain. A truncated cone/cylinder ring
// wrapping around the magnetic pole, axis-aligned with magnetic north.
// uv.y is the vertical axis along the curtain (bottom = surface, top =
// upper altitude); the shader paints longitudinally-folded vertical
// ribbons within it via fbm noise, with altitude-stratified colour and
// a feathered top edge — matches the look of real long-exposure aurora
// photography rather than a flat polar cap.
//
// Why a cone rather than a sphere band: a sphere band sits flat on the
// surface and reads as a yarmulke; the aurora's defining visual is its
// VERTICAL extent (curtains hanging from ~100km to ~300km altitude).
// The cone gives that vertical sheet shape so the ribbons can drape
// downward with feathered edges.
//
// Single mesh, alpha-blended, no particle pile-up — bloom-safe.

import { OKLAB_MIX_GLSL } from './oklab.js';
import { NOISE3D_GLSL } from './noise3d.js';

export const northernLightsVertexShader = `
  uniform float time;
  uniform vec3 sunDirLocal;
  uniform float asymmetry;
  uniform float foldPhase;
  uniform float foldFreq;

  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying vec3 vEarthCenter;
  varying float vWaveBoost;
  varying float vNightBias;

  void main() {
    vUv = uv;
    // Earth's centre in world space — modelMatrix * (0,0,0,1) gives
    // the world position of the parent (earthTiltGroup) origin, which
    // sits at Earth's centre. Used in the fragment to derive Earth's
    // SURFACE normal at the cylinder fragment, which is what the
    // day/night gate should care about — not the cylinder's own
    // horizontal-radial normal.
    vEarthCenter = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;

    // === MAGNETOTAIL ASYMMETRY ===
    // Real auroral ovals are NOT circular. The magnetosphere is
    // pulled into a long teardrop tail on the night side by the
    // solar wind, so particle precipitation broadens the oval
    // equatorward of the magnetic pole on the night side and
    // narrows it on the day side. We approximate by computing
    // the longitudinal angle between this vertex's position and
    // the sun direction (in magnetic-frame local space), then
    // stretching the cylinder radially outward when the vertex
    // is on the night side. Pushing the vertex outward effectively
    // moves it to a LOWER (more equatorial) latitude — the
    // teardrop shape every real space photo of the oval shows.
    float vertLon = atan(position.z, position.x);
    float sunLon = atan(sunDirLocal.z, sunDirLocal.x);
    // -cos(diff) is +1 when vertex is opposite the sun (night side),
    // -1 when vertex is sun-aligned (day side), 0 at dawn/dusk.
    float nightCos = -cos(vertLon - sunLon);
    float nightBias = max(nightCos, 0.0);
    // Smooth the bias so the day→night transition doesn't have a
    // hard discontinuity along the dawn/dusk meridian.
    nightBias = smoothstep(0.0, 1.0, nightBias);
    vNightBias = nightBias;
    float nightStretch = 1.0 + nightBias * asymmetry;

    // Wavy oval. The base geometry is a perfect circular cone wall
    // around the pole; in real life the auroral oval drifts and pulses
    // into an irregular shape with broader and narrower segments. We
    // sample a sum-of-sines wave keyed on longitude (atan2 of the
    // vertex's xz) and scale the position's xz by (1 + wave) so the
    // ring expands and contracts in/out radially. Three frequencies
    // give an organic non-repeating shape; small time offsets drift
    // the wave slowly.
    // Oval shape wave — broader/narrower segments drift around the
    // ring over time. Drift rates 4× the earlier values so the
    // ribbons visibly shift and the oval feels alive rather than
    // statically deformed.
    float wave =
      sin(vertLon * 3.0 + time * 0.22) * 0.55 +
      sin(vertLon * 5.0 - time * 0.31 + 1.7) * 0.30 +
      sin(vertLon * 11.0 + time * 0.48 + 4.3) * 0.12;
    float splitWave =
      sin(vertLon * (2.0 + foldFreq * 0.35) - time * 0.11 + foldPhase * 0.63) * 0.58 +
      sin(vertLon * (4.7 + foldFreq * 0.52) + time * 0.16 + foldPhase * 1.17) * 0.34 +
      sin(vertLon * 8.9 - time * 0.21 + foldPhase * 2.41) * 0.18;
    float shearWave =
      sin(vertLon * (1.6 + foldFreq * 0.28) + time * 0.08 + foldPhase * 1.91) * 0.55 +
      sin(vertLon * 6.4 - time * 0.14 + foldPhase * 0.77) * 0.24;
    float heightAlt = 1.0 - uv.y;
    float waveDamping = 1.0 - heightAlt * 0.55;
    float radialScale = (1.0 + wave * 0.10 * waveDamping + splitWave * 0.035) * nightStretch;
    vec3 displaced = position;
    displaced.xz *= radialScale;
    displaced.y += shearWave * 0.030 * length(position.xz) * (0.35 + heightAlt * 0.65);

    // Pass the per-vertex wave amplitude through so the fragment shader
    // can brighten the bulged-out segments slightly — real aurora arcs
    // crest brighter where they expand.
    vWaveBoost = clamp(wave * 0.5 + 0.5, 0.0, 1.0);

    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
    vWorldPosition = (modelMatrix * vec4(displaced, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
  }
`;

export const northernLightsFragmentShader = `
  ${OKLAB_MIX_GLSL}
  uniform float time;
  uniform float intensity;
  uniform vec3 sunPosition;
  // Per-curtain colour stratification. Each layer (main green oval,
  // diffuse shoulder) gets its own colour-set uniforms.
  uniform vec3 colBase;
  uniform vec3 colMid;
  uniform vec3 colTop;
  uniform float foldFreq;
  uniform float foldPhase;
  // diffuse: 0 → discrete fold-ribbon main oval. 1 → smooth uniform
  // glow for the diffuse shoulder layer (no high-contrast density
  // gaps). Lets the same shader source paint both layers cleanly.
  uniform float diffuse;

  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying vec3 vEarthCenter;
  varying float vWaveBoost;
  varying float vNightBias;

  // Cheap value-noise for organic curtain folds.
  float hash(float n) { return fract(sin(n) * 43758.5453123); }
  float noise1D(float x) {
    float i = floor(x);
    float f = fract(x);
    float u = f * f * (3.0 - 2.0 * f);
    return mix(hash(i), hash(i + 1.0), u);
  }
  float fbm1D(float x) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise1D(x);
      x *= 2.0;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    // uv.x = longitude around the polar ring [0,1]
    // uv.y is the mesh's vertical coordinate; converted below into
    // physical lower-edge-to-upper-edge altitude.
    float lon = vUv.x;
    float alt = vUv.y;
    // CylinderGeometry's UV orientation reads inverted in the rendered
    // cone: use a physical altitude coordinate so heightAlt=0 is the
    // Earth-facing lower edge and heightAlt=1 is the upper curtain.
    float heightAlt = 1.0 - alt;

    // === CURTAIN FOLDS — LIVE, ANIMATED ===
    // Real aurora is constantly in motion: the ribbons drift along
    // longitude as the magnetic field reconfigures, individual
    // strands brighten/dim on second-to-minute scales, and the
    // whole oval expands and contracts with solar wind variability.
    // Time multipliers cranked from the earlier slow drift (0.07,
    // 0.14, 0.22) to (0.35, 0.62, 0.95) so the ribbons visibly
    // shift on a few-second timescale — fast enough to read as
    // "alive" without strobing.
    float fold0 = fbm1D(lon * (6.0 * foldFreq)  + time * 0.35 + foldPhase);
    float fold1 = fbm1D(lon * (18.0 * foldFreq) + time * 0.62 + foldPhase + 11.3);
    float fold2 = fbm1D(lon * (52.0 * foldFreq) + time * 0.95 + foldPhase + 23.7);
    // Sub-second shimmer — additional high-frequency modulation
    // that adds the flickering character real aurora photography
    // captures. Independent phase so it doesn't lock to the main
    // fold drift. Applied to density below at low amplitude.
    float shimmer = fbm1D(lon * 90.0 + time * 2.4 + 47.1) * 0.5 + 0.5;

    // Base curtain sheet from the longitudinal fold noise. This is
    // deliberately softer than the visible filament mask below: real
    // aurora has a continuous luminous curtain behind the rayed
    // columns, not one opaque snake.
    float ribbonDensity = smoothstep(0.18, 0.88, fold0) *
                          mix(0.55, 1.00, fold1) *
                          mix(0.65, 1.00, fold2);
    float smoothDensity = 0.55 + 0.30 * fold1 + 0.15 * fold2;
    smoothDensity = clamp(smoothDensity, 0.0, 1.0);
    float curtainSheet = 0.20 + 0.56 * ribbonDensity;
    float density = mix(curtainSheet, smoothDensity, diffuse);

    // === MULTI-SCALE INDEPENDENT ACTIVATIONS ===
    // Real aurora is a chaotic patchwork: different longitude
    // sectors brighten and fade INDEPENDENTLY with no common
    // schedule and no two patches having the same hang time. We
    // simulate by sampling three independent noise fields at
    // unrelated spatial AND temporal scales, then transforming
    // each through a thresholded power curve. The smoothstep
    // gates pick out only the noise PEAKS (rare bright moments);
    // the pow() makes those peaks rise sharply and decay slowly
    // — what real auroral activation looks like.
    //
    // Three fields with progressively higher frequencies + faster
    // drift give a layered effect: slow large patches that linger
    // (~10s hang), medium patches (~4s hang), fast small flares
    // (~1-2s hang). Independent foldPhase offsets per layer keep
    // them from locking. Across the whole oval, you see different
    // sections lighting up at different moments with different
    // durations — no synchronised pulses anywhere.
    float act1 = fbm1D(lon * 2.5 + time * 0.18 + foldPhase + 5.3);
    float act2 = fbm1D(lon * 6.8 - time * 0.35 + foldPhase * 1.7 + 17.1);
    float act3 = fbm1D(lon * 14.2 + time * 0.62 + foldPhase * 2.3 + 31.4);
    // Long-hang activations (slow noise + low power = broad peaks)
    act1 = pow(smoothstep(0.42, 0.82, act1), 1.8);
    // Medium-hang activations (medium noise + medium power)
    act2 = pow(smoothstep(0.50, 0.88, act2), 2.4);
    // Short-hang flares (fast noise + high power = sharp brief spikes)
    act3 = pow(smoothstep(0.58, 0.94, act3), 3.2);
    // Combine into a chaotic activation field; the multiplicative
    // base ensures the curtain always has a baseline glow but
    // peaks brighten dramatically when activations overlap.
    float activation = 0.58 + act1 * 0.34 + act2 * 0.22 + act3 * 0.14;
    float broadArc = fbm1D(lon * 1.08 - time * 0.035 + foldPhase * 0.27 + 71.4);
    broadArc = smoothstep(0.20, 0.86, broadArc);
    float arcPresence = 0.34 + broadArc * 0.66;
    density *= activation;

    // Single-fragment substorm burst — derives from the act1 field
    // so it's coherent with the slow large-patch activations.
    // Drives an additional hue shift toward magenta during onset
    // (used in the colour block below).
    float burst = act1;

    // === FIELD-ALIGNED RAY FORMATION ===
    // The reference look is not a solid ribbon: it is a connected
    // curtain with narrow, nearly vertical striations running through
    // it. These are rayed aurora, visually tracing bundles of particle
    // precipitation along the magnetic field. The key is that the
    // striations should MODULATE a shared luminous sheet, not cut it
    // into isolated sticks.
    float flow = fbm1D(lon * 10.5 + heightAlt * 2.2 + time * 0.12 + foldPhase);
    float fineFlow = fbm1D(lon * 27.0 - heightAlt * 1.8 - time * 0.18 + foldPhase * 1.3);
    float rayCoord = lon * (132.0 * foldFreq) + foldPhase * 0.19 + time * 0.036 +
                     (flow - 0.5) * 1.25 + (fineFlow - 0.5) * 0.34;
    float rayWave = sin(rayCoord * 6.2831853);
    float fineWave = sin((rayCoord * 1.83 + flow * 2.4 + time * 0.08) * 6.2831853);
    float rayCore = pow(max(rayWave * 0.5 + 0.5, 0.0), 3.2);
    float rayHalo = pow(max(rayWave * 0.5 + 0.5, 0.0), 1.2);
    float fineCore = pow(max(fineWave * 0.5 + 0.5, 0.0), 4.2);
    float rayEnergy = 0.74 + 0.26 * fbm1D(lon * 6.0 + time * 0.42 + foldPhase + 4.6);
    float ray = clamp((rayCore * 0.16 + fineCore * 0.04) * rayEnergy, 0.0, 1.0);
    float filamentMask = clamp(rayHalo * 0.42 + rayCore * 0.08 + fineCore * 0.02, 0.0, 1.0);
    float ribbedSheet = 0.98 + filamentMask * 0.06;
    density *= mix(ribbedSheet, 1.0, diffuse * 0.80);
    density *= 1.0 + ray * 0.035;

    // === IRREGULAR BAND BREAKS ===
    // Real aurora is not perfectly uniform around the oval, but the
    // dim sectors should remain connected to the curtain. Earlier this
    // hard-cut the band and made the rays look like separate fries.
    // Keep it as a soft activity modulation only.
    float break1 = fbm1D(lon * 1.4 + time * 0.07 + foldPhase + 91.7);
    float break2 = fbm1D(lon * 3.1 - time * 0.05 + foldPhase * 0.9 + 113.2);
    float breakMask = smoothstep(0.30, 0.48, break1) * smoothstep(0.28, 0.46, break2);
    // Tertiary fast-drift break that flickers gaps in/out at
    // smaller scales — partial dim zones rather than full gaps.
    float break3 = fbm1D(lon * 6.2 + time * 0.14 + foldPhase * 1.7 + 137.9);
    breakMask *= mix(0.72, 1.0, smoothstep(0.32, 0.52, break3));
    density *= (0.66 + breakMask * 0.34) * arcPresence;

    density *= 1.0 + vNightBias * 0.20 * diffuse;

    // === DUST SHIMMER — irregular, not cyclic ===
    // 2D noise with INDEPENDENT drift rates per layer so the
    // shimmer never repeats. Layers run at unrelated speeds in
    // longitude AND altitude, producing chaotic particulate texture.
    vec2 dustUv = vec2(lon * 18.0 + time * 1.4, heightAlt * 12.0 - time * 0.9);
    float dustA = fbm1D(dustUv.x + dustUv.y * 1.3);
    vec2 dustUv2 = vec2(lon * 40.0 - time * 1.8, heightAlt * 28.0 + time * 1.3);
    float dustB = fbm1D(dustUv2.x - dustUv2.y * 0.7);
    float dustShimmer = 0.88 + (dustA * 0.55 + dustB * 0.45) * 0.24;
    density *= dustShimmer;
    // High-frequency flicker at low amplitude — the fast scintillation
    // long-exposure timelapses show along the curtain.
    density *= 0.96 + shimmer * 0.08;
    float connectedVeil = (0.13 + fold0 * 0.05 + fold1 * 0.04) * (1.0 - smoothstep(0.62, 1.0, heightAlt));
    density = max(density, mix(connectedVeil, smoothDensity * 0.84, diffuse));

    // Film/VFX-style separation: opacity is a continuous fog sheet,
    // while rays are emission detail inside that sheet. This avoids
    // the bright striations becoming separated visible bars.
    float veilActivity = 0.76 + act1 * 0.10 + act2 * 0.06;
    float veilBreak = (0.72 + breakMask * 0.28) * (0.45 + broadArc * 0.55);
    float opacitySheet = max(connectedVeil, curtainSheet * 0.42) * veilActivity * veilBreak;
    opacitySheet = max(opacitySheet, smoothDensity * 0.16 * (1.0 - smoothstep(0.64, 1.0, heightAlt)));
    float alphaDensity = mix(opacitySheet, smoothDensity * 0.42, diffuse);
    float emissionDensity = max(density * 0.72, alphaDensity * (0.78 + filamentMask * 0.22));

    // === VERTICAL SHAPE — real curtain photometry ===
    // Three traits from actual auroral physics:
    //   1. The LOWER BORDER is sharp (precipitating electrons stop
    //      abruptly where the atmosphere gets dense, ~100 km) — a
    //      tight 0-0.05 feather instead of the old soft 0.12 ramp.
    //   2. Luminosity PEAKS just above that border and decays
    //      roughly exponentially with altitude; the old uniform
    //      vertical brightness is the main reason painted curtains
    //      read as "ribbon" instead of "curtain".
    //   3. The TOP is jagged, not a constant-height band: discrete
    //      rays and active patches reach much higher than quiet
    //      sections (field-aligned acceleration), so the visible
    //      top height is driven by the ray + slow-activation
    //      fields. Quiet stretches fade out by ~0.62, strong rays
    //      hold visibility into the magenta zone near the cap.
    float baseFeather = smoothstep(0.0, 0.05, heightAlt);
    float columnDecay = exp(-max(heightAlt - 0.06, 0.0) * 3.4);
    float heightDrive = clamp(rayHalo * 0.22 + act1 * 0.55 + burst * 0.25, 0.0, 1.0);
    float topStart = mix(0.64, 0.88, heightDrive);
    float topFade  = 1.0 - smoothstep(topStart, min(topStart + 0.28, 1.0), heightAlt);
    float verticalShape = baseFeather * topFade * mix(1.0, columnDecay, 0.82);

    // === ALTITUDE-STRATIFIED COLOUR (green bottom → magenta top) ===
    // Real ISS / long-exposure aurora photos show the classic
    // split: green (oxygen 558 nm) dominates the bottom 100-300 km,
    // magenta-purple (oxygen 630 nm at >300 km plus high-altitude
    // nitrogen) fades in above. We map that split to a clear
    // smoothstep transition centred at alt ~0.5 so the bottom half
    // is green and the top half is magenta. OKLCh-bridged so the
    // perceptual hue arc keeps both halves vivid through the
    // transition.
    //
    // Green lower curtain, reddish-magenta upper veil. Keep the
    // base mostly oxygen green so the top color reads as a physical
    // altitude layer instead of random patches painted on the sheet.
    vec3 greenZone = oklchMix(colBase, colMid, 0.22);
    float greenPulse = fbm1D(lon * 4.4 - time * 0.13 + foldPhase + 61.8);
    vec3 zoneBase = oklchMix(colBase, greenZone, 0.25 + greenPulse * 0.25);

    // Per-region magenta altitude threshold: some columns pick up
    // red/magenta high in the curtain, while others stay mostly
    // green. The upper color is softer than the green rays because
    // high-altitude red oxygen emission washes out fine structure.
    float magentaThresholdNoise = fbm1D(lon * 3.2 + time * 0.10 + foldPhase + 5.7);
    float magentaStart = mix(0.58, 0.74, magentaThresholdNoise);
    float magentaMix = smoothstep(magentaStart, magentaStart + 0.24, heightAlt);
    float intensityNoise = fbm1D(lon * 5.4 - time * 0.18 + foldPhase * 1.4 + 23.1);
    magentaMix *= mix(0.38, 1.00, intensityNoise);
    magentaMix *= 0.62 + rayHalo * 0.24;
    magentaMix = clamp(magentaMix, 0.0, 1.0);

    float baseAltMix = smoothstep(0.0, 0.42, heightAlt);
    vec3 col = oklchMix(zoneBase, colMid, baseAltMix * 0.62);
    col = oklchMix(col, colTop, magentaMix);

    // Pure-white-tipped rays — the brightest discrete ray-streamers
    // in real aurora photos read as white-cored before fading to
    // colour at the edges.
    vec3 rayColor = vec3(1.0, 0.95, 0.85);
    col = oklchMix(col, rayColor, ray * 0.10);

    // Burst-driven onset shift — substorm bursts briefly bias the
    // colour toward magenta in addition to brightening, matching
    // the way real onset events flash pink-red through the curtain.
    col = oklchMix(col, colTop, burst * 0.04);

    // Pink nitrogen fringe on the LOWER border. During strong
    // activity, high-energy electrons punch below the green oxygen
    // layer and excite molecular nitrogen, painting a thin
    // pink-crimson rim along the curtain's bottom edge — the
    // signature of every dramatic storm photo (and absent in quiet
    // arcs, so it's gated by the activation field).
    float fringeBand = (1.0 - smoothstep(0.05, 0.16, heightAlt)) * baseFeather;
    float fringeGate = smoothstep(0.95, 1.55, activation);
    vec3 fringePink = vec3(1.0, 0.42, 0.58);
    col = oklchMix(col, fringePink, fringeBand * fringeGate * 0.06);

    // === NIGHT-SIDE GATING ===
    // Only visible on Earth's dark hemisphere — day-side scatter
    // (the blue atmosphere) would otherwise overwhelm the curtain.
    // Gate uses EARTH'S SURFACE normal at the fragment's footprint,
    // not the cylinder's own normal. The cylinder's normal points
    // horizontally outward from its Y axis, so half of its
    // longitudinal sides always register as "night-facing" even
    // when the underlying Earth surface is fully sunlit — that's
    // what produced the dashed "hair" arcs visible on Antarctica
    // when the south pole was in day. Deriving the surface normal
    // from (vWorldPosition - vEarthCenter) gives the correct
    // day/night state per fragment.
    vec3 sunDir = normalize(sunPosition - vWorldPosition);
    vec3 earthSurfaceNormal = normalize(vWorldPosition - vEarthCenter);
    float sunFacing = dot(earthSurfaceNormal, sunDir);
    // Very narrow feather zone — sunFacing ≥ -0.05 means day or
    // terminator → fully invisible. Below -0.10 → fully visible.
    // The 0.05-wide partial-visibility band is too narrow for the
    // fold-density noise pattern to show as discrete beaded pixels
    // at the terminator, which was the persistent hair-artifact
    // source even after switching to surface-normal gating.
    float nightVisibility = smoothstep(-0.05, -0.10, sunFacing);

    // Bulged-out segments of the oval (where the wave expanded the
    // ring) get a small brightness boost — real aurora arcs crest
    // brighter at their outermost extent.
    float waveBrightness = 0.85 + vWaveBoost * 0.30;

    // Curtains read brightest when seen edge-on. Fade the broad sheet
    // when the camera looks through it face-on so close views do not
    // become a red transparent wall.
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float edgeOn = pow(1.0 - abs(dot(normalize(vWorldNormal), viewDir)), 0.65);
    float viewVisibility = 0.04 + edgeOn * 0.96;

    float alpha = alphaDensity * verticalShape * nightVisibility * intensity * waveBrightness * viewVisibility;
    // Keep the faint veil connected while still trimming near-zero
    // fragments at the cone edges.
    if (alpha < 0.004) discard;

    float emissionBoost = 0.92 + emissionDensity * 0.18 + filamentMask * 0.22 + ray * 0.34;
    gl_FragColor = vec4(col * emissionBoost, alpha);
  }
`;

export const auroraVertexShader = `
  uniform float time;
  uniform vec3 sunPosition;
  uniform float intensity;

  attribute float particleSize;
  attribute float particleAlpha;
  attribute float particlePhase;
  attribute float particleHeight;
  attribute vec3 color;

  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec3 localNormal = normalize(position);
    float fold = sin(particlePhase + time * 1.25) * sin(particlePhase * 0.37 - time * 0.55);
    vec3 displaced = position + localNormal * fold * 0.028 * particleHeight;

    vec3 worldPosition = (modelMatrix * vec4(displaced, 1.0)).xyz;
    vec3 worldNormal = normalize(mat3(modelMatrix) * normalize(displaced));
    vec3 sunDir = normalize(sunPosition - worldPosition);
    float sunFacing = dot(worldNormal, sunDir);
    float nightVisibility = smoothstep(0.12, -0.16, sunFacing);

    vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = clamp(particleSize * (220.0 / max(1.0, -mvPosition.z)), 1.0, 14.0);

    vColor = color;
    vAlpha = particleAlpha * nightVisibility * intensity;
  }
`;

export const auroraFragmentShader = `
  uniform float time;

  varying vec3 vColor;
  varying float vAlpha;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 centered = gl_PointCoord - vec2(0.5);
    float dist = length(centered);
    if (dist > 0.48) discard;

    float softDisc = smoothstep(0.48, 0.0, dist);
    float core = smoothstep(0.18, 0.0, dist);
    float grain = 0.82 + noise(gl_PointCoord * 8.0 + vec2(time * 0.07, -time * 0.04)) * 0.18;
    float alpha = vAlpha * softDisc * grain;
    vec3 color = vColor * (0.58 + core * 0.72);

    if (alpha < 0.004) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

// Earth's aurora as light, not geometry: emission in a shell 85 to 330 km
// up, integrated along each view ray (createEarthAuroras in main.js puts a
// sphere at the shell's top round the Earth mesh, in the mesh's own frame).
//
// Where it is. The ovals sit round the geomagnetic (dipole) poles, fixed
// to the Sun while the Earth turns under them: a quiet-to-moderate night's
// discrete arcs at about 75 degrees magnetic latitude at magnetic noon and
// 67 at midnight, a little lower in the brightenings, with a band of
// diffuse aurora equatorward of them after midnight. Each arc is a soft
// ribbon (a bundle of sheets, ~10 km to its 1/e edge, more or less along
// the oval) on field lines that lean equatorward with height (about 21 km
// per 100 km at these latitudes), folded and curled along the oval, with
// a faint wider veil round it. Everything round the oval is noise in
// magnetic local time that wraps a whole number of cells, so midnight has
// no seam.
//
// What colour. Emission by height, optically thin: 557.7 nm atomic oxygen
// green from a sharp lower edge near 100 km (lower where the electrons
// are harder) fading over ~20-55 km, carried higher in the rays; 630 nm
// atomic oxygen red above ~150 km, a faint glow round each ribbon and a
// broad layer a few hundred km across the main arc, which is faint from
// above and shows on the limb, where the line of sight runs along it; N2+
// 427.8 nm blue-violet at the lower edge and the N2 first positive pink
// hem where the edge dips below ~100 km; and where a curtain's top stands
// above the Earth's shadow (the twilit flanks), N2+ in sunlight, which
// turns the rays' tops violet.
//
// How it is integrated. Each ribbon is integrated analytically across each
// step (erf of the distance to it at the step's two ends) and its line
// profiles over the step's height range, so the result does not depend on
// where the steps fall. The steps adapt to the distance from the nearest
// ribbon: never longer than that distance over the field's steepest rate
// of change (so no crossing is ever stepped over), never shorter than a
// ribbon's width. There is no coarse pass and nothing is cached between
// steps, so every quantity is a continuous function of the ray: no edge
// can come out stair-stepped. Ribbons and rays widen to the pixel's
// footprint with their column kept, so nothing shimmers at range.
//
// When it shows. Over ground the Sun has left (full once it is 14 degrees
// down, gone at 6), not where the line of sight passes over a twilit or
// sunlit limb (that sky outshines it), and not near the Sun when the Sun
// itself is in view: a camera exposed for the Sun records none of it.
export const earthAuroraVertexShader = `
  varying vec3 vLocal;

  void main() {
    vLocal = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const earthAuroraFragmentShader = `
  uniform vec3 uCamLocal;
  uniform float uR;
  uniform vec3 uSunLocal;
  uniform vec3 uMagAxis;
  uniform vec3 uMagE1;
  uniform vec3 uMagE2;
  uniform vec2 uSunMagCS;
  uniform float uTime;
  uniform float uPixelAngle;
  uniform float uIntensity;
  uniform float uActivity;
  uniform float uSunVisible;
  // The oval's shape round magnetic local time (src/spaceweather/oval.js):
  // per hemisphere, north first, five vec4s of Fourier coefficients for the
  // main arc's latitude and the arcs' brightness by sector. The model's own
  // shape unless live data drives it (src/spaceweather/viewer.js).
  uniform vec4 uOval[10];
  // cos^2 of the widest the ovals reach from the magnetic axis (the march's
  // polar clip): 0.5625 (48.6 degrees magnetic latitude) unless a storm
  // pushes them further.
  uniform float uConeC2;

  varying vec3 vLocal;

  ${NOISE3D_GLSL}

  const float EARTH_KM = 6371.0;
  const float SHELL_LO = 85.0;
  const float SHELL_HI = 330.0;
  const float AU_PI = 3.14159265;
  // Line colours, linear: 557.7 nm O, 630.0 nm O, 427.8 nm N2+, N2 1P. The
  // green and red are as ISS crew cameras record them, measured in OKLCh on
  // NASA photographs: the green's hue is 131 to 147 (iss040e111251 and
  // iss030e119777 at 142 to 147, the newer iss072e188141, iss073e0247726 and
  // iss075e088592 at 131 to 137), so C_GREEN sits at 140, not a pure
  // spectral one; the red layer is magenta-red (hue 327 to 352) with the
  // blue lines.
  const vec3 C_GREEN = vec3(0.36, 1.0, 0.27);
  const vec3 C_RED = vec3(1.0, 0.16, 0.36);
  const vec3 C_BLUE = vec3(0.32, 0.10, 1.0);
  const vec3 C_PINK = vec3(1.0, 0.12, 0.40);
  // A ribbon's half-width to 1/e (km) and its red upper glow's.
  const float SHEET_W = 10.0;
  const float RED_W = 26.0;
  // The broad 630 nm layer's half-width (km) across the main arc: soft
  // electrons over a wide band and O(1D)'s 110 s lifetime spread the red
  // over hundreds of km, so it is faint from above and reads on the limb,
  // where a tangent line of sight runs through a thousand km or more of it.
  const float RED_BROAD_W = 190.0;
  // The Earth's shadow edge for sunlit aurora: sunlight below about 60 km
  // is screened by the lower air.
  const float SHADOW_SCREEN_KM = 60.0;
  // The sheet field's steepest change per km along a ray (the latitude
  // term, the lean, the oval's own shape and the folds' slopes), and the
  // most the folds alone can add to it.
  const float FIELD_LIP = 2.2;
  const float FOLD_SLOPE = 0.75;
  // Longest step (km): the profiles and the diffuse band are smooth on it.
  const float MAX_STEP_KM = 120.0;

  // A hash without sine (Hoskins), cheap enough to call a few hundred times
  // per pixel; its inputs stay under ~1e4.
  float auHash(float n) {
    n = fract(n * 0.1031);
    n *= n + 33.33;
    n *= n + n;
    return fract(n);
  }
  vec3 auHash3(vec3 n) {
    n = fract(n * 0.1031);
    n *= n + 33.33;
    n *= n + n;
    return fract(n);
  }

  // Value noise round the oval: x in cells, wrapping every period cells,
  // y (time) unbounded. s picks an independent field.
  float auNoise(float x, float y, float period, float s) {
    float i = floor(x);
    float j = floor(y);
    float fx = fract(x);
    float fy = fract(y);
    fx = fx * fx * (3.0 - 2.0 * fx);
    fy = fy * fy * (3.0 - 2.0 * fy);
    float i0 = mod(i, period);
    float i1 = mod(i + 1.0, period);
    float a = auHash(i0 + j * 157.0 + s);
    float b = auHash(i1 + j * 157.0 + s);
    float c = auHash(i0 + (j + 1.0) * 157.0 + s);
    float d = auHash(i1 + (j + 1.0) * 157.0 + s);
    return mix(mix(a, b, fx), mix(c, d, fx), fy);
  }

  // Three value noises on the same cells with independent seeds, sharing
  // the lattice work (the arcs' own curls).
  vec3 auNoise3(float x, float y, float period, vec3 s) {
    float i = floor(x);
    float j = floor(y);
    float fx = fract(x);
    float fy = fract(y);
    fx = fx * fx * (3.0 - 2.0 * fx);
    fy = fy * fy * (3.0 - 2.0 * fy);
    float i0 = mod(i, period);
    float i1 = mod(i + 1.0, period);
    vec3 a = auHash3(vec3(i0 + j * 157.0) + s);
    vec3 b = auHash3(vec3(i1 + j * 157.0) + s);
    vec3 c = auHash3(vec3(i0 + (j + 1.0) * 157.0) + s);
    vec3 d = auHash3(vec3(i1 + (j + 1.0) * 157.0) + s);
    return mix(mix(a, b, fx), mix(c, d, fx), fy);
  }

  float auErf(float x) {
    float x2 = x * x;
    float ax2 = 0.147 * x2;
    return sign(x) * sqrt(1.0 - exp(-x2 * (1.27324 + ax2) / (1.0 + ax2)));
  }

  // Column of a Gaussian sheet of half-width w crossed by a step of
  // stepKm along which the distance to it runs linearly from da to db.
  float auSlab(float da, float db, float w, float stepKm) {
    float dd = db - da;
    if (abs(dd) < 0.02 * w) {
      float dm = 0.5 * (da + db);
      return stepKm * exp(-dm * dm / (w * w));
    }
    return stepKm * 0.886227 * w * (auErf(db / w) - auErf(da / w)) / dd;
  }

  struct AuPoint {
    float hKm;
    float magLat;
    float hemi;
    float cT;
    float sT;
    float tau;
    float midnight;
    float lat0;
    float sector;
    float night;
    vec3 n;
  };

  // The oval's series at an angle from magnetic noon given as (cos, sin):
  // x the main arc's magnetic latitude, y the arcs' brightness there.
  vec2 auOval(float hemi, float c1, float s1) {
    vec2 h2 = vec2(c1 * c1 - s1 * s1, 2.0 * c1 * s1);
    vec2 h3 = vec2(c1 * h2.x - s1 * h2.y, s1 * h2.x + c1 * h2.y);
    vec2 h4 = vec2(h2.x * h2.x - h2.y * h2.y, 2.0 * h2.x * h2.y);
    vec4 lo = vec4(c1, s1, h2);
    vec4 hi = vec4(h3, h4);
    vec4 o0 = hemi > 0.0 ? uOval[0] : uOval[5];
    vec4 o1 = hemi > 0.0 ? uOval[1] : uOval[6];
    vec4 o2 = hemi > 0.0 ? uOval[2] : uOval[7];
    vec4 o3 = hemi > 0.0 ? uOval[3] : uOval[8];
    vec4 o4 = hemi > 0.0 ? uOval[4] : uOval[9];
    return vec2(o0.x + dot(o1, lo) + dot(o2, hi), o0.y + dot(o3, lo) + dot(o4, hi));
  }

  AuPoint auPoint(vec3 p) {
    AuPoint q;
    float r = length(p);
    q.n = p / r;
    q.hKm = (r / uR - 1.0) * EARTH_KM;
    float sinLat = dot(q.n, uMagAxis);
    q.hemi = sinLat >= 0.0 ? 1.0 : -1.0;
    q.magLat = degrees(asin(min(abs(sinLat), 1.0)));
    // The angle from magnetic noon, as (cos, sin) and as an angle.
    float mx = dot(q.n, uMagE1);
    float my = dot(q.n, uMagE2);
    float inv = inversesqrt(max(mx * mx + my * my, 1e-12));
    mx *= inv;
    my *= inv;
    q.cT = mx * uSunMagCS.x + my * uSunMagCS.y;
    q.sT = my * uSunMagCS.x - mx * uSunMagCS.y;
    q.tau = atan(q.sT, q.cT);
    q.midnight = 0.5 - 0.5 * q.cT;
    // Discrete arcs: ~75 deg at magnetic noon, ~67 at midnight, a little
    // lower in the brightenings (the model's series is 72 + 3.2 cT; live
    // data gives the real shape). The arcs' brightness by sector is the
    // model's 0.12 + 0.88 * midnight, or the nowcast's.
    vec2 oval = auOval(q.hemi, q.cT, q.sT);
    q.lat0 = oval.x - 1.3 * uActivity * q.midnight;
    q.sector = max(oval.y, 0.03);
    // Visible over ground the Sun has left: gone with it 6 degrees down,
    // full at 14 (twilight outshines it before that).
    q.night = smoothstep(-0.10, -0.24, dot(q.n, uSunLocal));
    return q;
  }

  // The oval's x coordinate in noise cells for a period round the circle.
  float auCells(float tau, float period) {
    return (tau / (2.0 * AU_PI) + 0.5) * period;
  }

  // Distances (km, poleward) from the three arcs' centre lines: the main
  // arc, one poleward and one equatorward, further apart on the night side,
  // with the field lines' lean, folds of ~570 and ~200 km shared by the arcs
  // (an arc system folds together) and curls of ~110 km of each arc's own,
  // all evolving over minutes. Their curvature is what the march's step
  // floor is set against: no dip of a ribbon's depth is shorter than it.
  vec3 auSheets(AuPoint q, float seed) {
    float base = (q.magLat - q.lat0) * 111.2 + 0.21 * (q.hKm - 100.0)
      - 110.0 * (auNoise(auCells(q.tau, 24.0), uTime * 0.001, 24.0, seed) - 0.5)
      - 36.0 * (auNoise(auCells(q.tau, 70.0), uTime * 0.003, 70.0, seed + 7.0) - 0.5);
    float zone = (0.55 + 0.9 * q.midnight + 0.5 * uActivity) * 111.2;
    vec3 own = auNoise3(auCells(q.tau, 120.0), uTime * 0.006, 120.0, vec3(seed + 13.0, seed + 44.0, seed + 75.0));
    return vec3(base, base - 0.9 * zone, base + 0.8 * zone) - 12.0 * (own - 0.5);
  }

  // Rays along a ribbon: field-aligned (long in height), kinks ~40 km
  // apart over rays of ~12 and ~4 km, drifting along the arc. Ridged
  // simplex on the oval's circle; an octave under about two pixels is its
  // mean (0.344) and is not evaluated.
  float auRays(AuPoint q, float fi, float seed, float fpKm) {
    float drift = uTime * 0.00025 * (1.0 + 0.6 * fi);
    float cd = cos(drift);
    float sd = sin(drift);
    float ca = q.cT * cd - q.sT * sd;
    float sa = q.sT * cd + q.cT * sd;
    float sum = 0.0;
    float norm = 0.0;
    float amp = 1.0;
    float rayKm = 40.0;
    for (int o = 0; o < 3; o++) {
      float detail = 1.0 - smoothstep(0.25, 0.6, fpKm / rayKm);
      float ridge = 0.344;
      if (detail > 0.0) {
        float rad = 2400.0 / rayKm;
        vec3 c = vec3(ca * rad, sa * rad, q.hKm * 0.003 + fi * 9.0 + seed + float(o) * 3.3);
        float r = 1.0 - abs(snoise(c));
        r *= r;
        r *= r;
        ridge = mix(0.344, r, detail);
      }
      sum += amp * ridge;
      norm += amp;
      rayKm /= 3.2;
      amp *= 0.5;
    }
    return sum / norm;
  }

  // Height profiles of the lines (per km of column, peak 1) and their
  // integrals, so a step that runs along a ribbon (a view from above)
  // takes the profile's mean over its height range rather than one point.
  // An edge profile: zero below foot, a smoothstep ramp over rampW, flat
  // for flatW, then an exponential fall with scale decay.
  float auEdgeCum(float h, float foot, float rampW, float flatW, float decay) {
    float x = clamp((h - foot) / rampW, 0.0, 1.0);
    float ramp = rampW * (x * x * x - 0.5 * x * x * x * x);
    float plateau = clamp(h - foot - rampW, 0.0, flatW);
    float above = max(h - foot - rampW - flatW, 0.0);
    return ramp + plateau + decay * (1.0 - exp(-above / decay));
  }
  float auEdge(float h, float foot, float rampW, float flatW, float decay) {
    return smoothstep(foot, foot + rampW, h) * exp(-max(h - foot - rampW - flatW, 0.0) / decay);
  }
  // Mean of the edge profile over [ha, hb], or its value when they meet.
  float auEdgeMean(float ha, float hb, float foot, float rampW, float flatW, float decay) {
    if (abs(hb - ha) < 0.5) return auEdge(0.5 * (ha + hb), foot, rampW, flatW, decay);
    return (auEdgeCum(hb, foot, rampW, flatW, decay) - auEdgeCum(ha, foot, rampW, flatW, decay)) / (hb - ha);
  }
  // Mean over [ha, hb] of a Gaussian band exp(-((h - c) / w)^2).
  float auBandMean(float ha, float hb, float c, float w) {
    if (abs(hb - ha) < 0.25 * w) { float m = (0.5 * (ha + hb) - c) / w; return exp(-m * m); }
    return 0.886227 * w * (auErf((hb - c) / w) - auErf((ha - c) / w)) / (hb - ha);
  }

  // Where along a step [0, 1] a Gaussian ribbon's light is centred, the
  // distance to it running linearly from da to db: the weighted mean of u
  // under exp(-(d(u) / w)^2), in closed form. 0.5 when the step runs along
  // the ribbon, the crossing point when it cuts across. Continuous in every
  // input, so which step a crossing falls in changes nothing.
  float auCentroid(float da, float db, float w) {
    float dd = db - da;
    if (abs(dd) < 0.05 * w) return 0.5;
    float xa = da / w;
    float xb = db / w;
    float ea = auErf(xb) - auErf(xa);
    if (abs(ea) < 1e-5) return abs(da) < abs(db) ? 0.0 : 1.0;
    float num = w * (exp(-xa * xa) - exp(-xb * xb)) / (1.7724539 * ea) - da;
    return clamp(num / dd, 0.0, 1.0);
  }

  float auHeight(vec3 p) { return (length(p) / uR - 1.0) * EARTH_KM; }

  // The light of one step [a, b] of the ray. Every factor of a ribbon is
  // taken at the centre of its light along the step, and the line profiles
  // averaged over the heights its light spans there, so the result is the
  // same however the steps fall.
  vec3 auStep(AuPoint qa, AuPoint qb, vec3 dA, vec3 dB, vec3 ro, vec3 rd, float ta, float tb, float kmPerUnit, float seed) {
    vec3 acc = vec3(0.0);
    float segKm = (tb - ta) * kmPerUnit;
    float fp = tb * uPixelAngle * kmPerUnit;
    float fpA = 1.5 * fp;
    for (int k = 0; k < 3; k++) {
      float fk = float(k);
      float s = seed + fk * 31.0;
      float da = dA[k];
      float db = dB[k];
      float near = da * db <= 0.0 ? 0.0 : min(abs(da), abs(db));
      if (near > 3.5 * sqrt(RED_W * RED_W + fpA * fpA)) continue;
      // The ribbon's own width is read at the step's nearer end first, then
      // everything at the centre of its light.
      float wGuess = sqrt(SHEET_W * SHEET_W + fpA * fpA);
      float u = auCentroid(da, db, wGuess);
      float tc = mix(ta, tb, u);
      AuPoint c = auPoint(ro + rd * tc);
      if (c.night <= 0.0 || c.hKm < SHELL_LO) continue;
      // Bright and broken stretches of each arc, a few hundred km long with
      // dimmer gaps between: most of an arc is faint and a little of it
      // bright. The night side's are the strongest; the poleward and
      // equatorward arcs are fainter.
      float stretch = smoothstep(0.1, 0.75, auNoise(auCells(c.tau, 20.0), uTime * 0.0008, 20.0, s + 41.0));
      float knots = auNoise(auCells(c.tau, 70.0), uTime * 0.0015, 70.0, s + 97.0);
      float strength = (k == 0 ? 1.0 : (k == 1 ? 0.4 : 0.28))
        * stretch * (0.3 + 1.4 * knots * knots)
        * c.sector * (0.55 + 0.7 * uActivity) * c.night;
      // The poleward and equatorward arcs run beside the main one only along
      // some sectors (cells of ~40 deg of magnetic local time), as in the
      // VIIRS night views: elsewhere one arc carries the oval, instead of
      // three parallel rings all the way round.
      if (k > 0) strength *= smoothstep(0.42, 0.72, auNoise(auCells(c.tau, 9.0), uTime * 0.0005, 9.0, s + 151.0));
      if (strength <= 0.001) continue;
      // Each ribbon's width varies along it.
      float w0 = SHEET_W * (0.65 + 0.7 * auNoise(auCells(c.tau, 60.0), uTime * 0.002, 60.0, s + 23.0));
      float wE = sqrt(w0 * w0 + fpA * fpA);
      float wV = sqrt(9.0 * w0 * w0 + fpA * fpA);
      float wrE = sqrt(RED_W * RED_W + fpA * fpA);
      // The heights the ribbon's light spans along this step.
      float su = min(0.7071 * wE / max(abs(db - da), 1e-3), 0.5);
      float ha = auHeight(ro + rd * mix(ta, tb, clamp(u - su, 0.0, 1.0)));
      float hb = auHeight(ro + rd * mix(ta, tb, clamp(u + su, 0.0, 1.0)));
      // Harder electrons push the lower edge down toward 93 km.
      float energy = smoothstep(0.45, 0.85, auNoise(auCells(c.tau, 40.0), uTime * 0.003, 40.0, s + 59.0))
        * (0.35 + 0.65 * uActivity);
      float h0 = 104.0 - 11.0 * energy;
      float rays = near < 3.5 * wV ? auRays(c, fk, s, tc * uPixelAngle * kmPerUnit) : 0.344;
      // Rays brighten the ribbon a little and carry the green higher.
      float rayMod = 0.8 + 0.58 * rays;
      float hg = 24.0 + 26.0 * rays;
      float green = auEdgeMean(ha, hb, h0 - 3.0, 8.0, 3.0, hg);
      float blue = auEdgeMean(ha, hb, h0 - 3.0, 6.0, 0.0, 14.0);
      float hem = auBandMean(ha, hb, h0 + 1.5, 4.5) * smoothstep(101.0, 95.0, h0);
      float red = smoothstep(150.0, 215.0, c.hKm) * exp(-max(c.hKm - 235.0, 0.0) / 60.0);
      // Sunlit aurora: where a curtain's upper part stands above the Earth's
      // shadow, its N2+ ions scatter sunlight resonantly (391.4 and 427.8
      // nm) and the rays' tops turn violet (iss075e088592 by the terminator).
      // The shadow's height over this point, then the profile's mean over
      // the heights the ribbon's light spans, as for the green.
      float sAlong = dot(c.n, uSunLocal);
      float hShadow = ((1.0 + SHADOW_SCREEN_KM / EARTH_KM) * inversesqrt(max(1.0 - sAlong * sAlong, 1e-4)) - 1.0) * EARTH_KM;
      float sunlit = auEdgeMean(ha, hb, max(hShadow - 15.0, 150.0), 50.0, 20.0, 40.0);
      float sheet = (w0 / wE) * auSlab(da, db, wE, segKm);
      float veil = (3.0 * w0 / wV) * auSlab(da, db, wV, segKm);
      float glow = (RED_W / wrE) * auSlab(da, db, wrE, segKm);
      acc += strength * (
        sheet * rayMod * (C_GREEN * green + C_BLUE * (0.10 * blue) + C_PINK * (0.6 * hem))
        + sheet * C_BLUE * (0.5 * sunlit * (0.15 + 0.85 * rays))
        + veil * 0.10 * C_GREEN * green
        + glow * C_RED * (0.022 * red * (0.6 + 0.4 * rays)));
    }
    // Diffuse aurora equatorward of the arcs, patchy, strongest after
    // midnight: smooth over hundreds of km, so sampled at the step's middle.
    // The broad red layer: its column across the main arc (centred 60 km
    // poleward of it) integrated over the step with erf, its heights
    // averaged over the step's (a step's chord dips under 0.3 km), only its
    // slow factors at the middle.
    float diffuse = 0.0;
    float redBroad = 0.0;
    {
      AuPoint q = auPoint(ro + rd * (0.5 * (ta + tb)));
      // How the sector brightness departs from the model's here (1 for the
      // model): the diffuse band and the red layer follow it.
      float rho = min(q.sector / (0.12 + 0.88 * q.midnight), 4.0);
      if (q.night > 0.0) {
        float across = auSlab(dA.x - 60.0, dB.x - 60.0, RED_BROAD_W, segKm);
        if (across > 1e-3) {
          // Growing out of the green's top, brightest where the main arc's
          // bright stretches are (its stretch field, same seed).
          float prof = auEdgeMean(qa.hKm, qb.hKm, 145.0, 75.0, 25.0, 40.0);
          float arcLit = smoothstep(0.0, 0.8, auNoise(auCells(q.tau, 20.0), uTime * 0.0008, 20.0, seed + 41.0));
          redBroad = across * prof * (0.35 + 0.65 * arcLit) * (0.2 + 0.8 * q.midnight) * rho * (0.4 + 0.8 * uActivity) * q.night;
        }
      }
      float dd = q.magLat - q.lat0;
      float band = smoothstep(-7.0, -4.0, dd) * (1.0 - smoothstep(-1.6, -0.4, dd));
      if (band > 0.0 && q.night > 0.0) {
        float patches = smoothstep(0.3, 0.8, auNoise(auCells(q.tau, 160.0), dd * 1.6 + uTime * 0.002, 160.0, seed + 211.0));
        float dawn = 0.4 + 0.6 * smoothstep(-0.2, 0.9, -q.sT);
        float prof = smoothstep(98.0, 112.0, q.hKm) * exp(-max(q.hKm - 120.0, 0.0) / 28.0);
        diffuse = band * mix(0.35, 1.0, patches) * dawn * q.midnight * rho * prof * (0.5 + 0.8 * uActivity) * q.night;
      }
    }
    // The layer's colour is the 630 nm red with the N2+ blue the crew
    // cameras record with it (hue ~346 in OKLCh, the photographs' 327-352).
    acc += C_GREEN * (0.004 * segKm * diffuse) + (C_RED + 0.25 * C_BLUE) * (0.0045 * redBroad);
    return acc;
  }

  void main() {
    if (uIntensity <= 0.0) discard;
    vec3 ro = uCamLocal;
    vec3 rd = normalize(vLocal - ro);
    float Ro = uR * (1.0 + SHELL_HI / EARTH_KM);
    float Ri = uR * (1.0 + SHELL_LO / EARTH_KM);
    float b = dot(ro, rd);
    float rr = dot(ro, ro);
    float disO = b * b - (rr - Ro * Ro);
    if (disO <= 0.0) discard;
    float sO = sqrt(disO);
    float t0 = max(-b - sO, 0.0);
    float t1 = -b + sO;
    if (t1 <= 0.0) discard;
    float disG = b * b - (rr - uR * uR);
    bool hitsGround = disG > 0.0 && -b - sqrt(disG) > 0.0;
    float disI = b * b - (rr - Ri * Ri);
    if (disI > 0.0) {
      float sI = sqrt(disI);
      if (rr > Ri * Ri) {
        if (-b - sI > 0.0) t1 = min(t1, -b - sI);
      } else {
        // Under the shell (the viewer's lowest views): from the shell's
        // floor up, and never through the ground.
        if (hitsGround) discard;
        t0 = max(t0, -b + sI);
      }
    }
    if (t1 <= t0) discard;

    // What lies behind the aurora along this ray. Over the limb (the ray
    // misses the ground and grazes the lower air) a twilit or sunlit limb
    // outshines it; and with the Sun itself in view, the rays near it
    // carry nothing a camera exposed for the Sun would record.
    float gate = 1.0;
    if (!hitsGround && b < 0.0) {
      vec3 pT = ro - rd * b;
      float hT = (length(pT) / uR - 1.0) * EARTH_KM;
      float lit = smoothstep(-0.30, -0.06, dot(normalize(pT), uSunLocal));
      gate *= 1.0 - lit * (1.0 - smoothstep(60.0, 160.0, hT));
    }
    // Only inside the Sun's glare (about 20 degrees), fixed to the sky.
    gate *= 1.0 - uSunVisible * smoothstep(0.93, 0.99, dot(rd, uSunLocal));
    if (gate < 0.002) discard;

    // Only the polar regions hold any: clip the ray to the double cone
    // within ~41 degrees of the magnetic axis, |n . m| > 0.75 (the oval's
    // diffuse edge never reaches below ~58 degrees in the model; a storm's
    // ovals widen it through uConeC2), a quadratic in t (taking the hull
    // when the ray passes through both nappes).
    {
      float CONE_C2 = uConeC2;
      float rm = dot(rd, uMagAxis);
      float om = dot(ro, uMagAxis);
      float qa = rm * rm - CONE_C2;
      float qb = 2.0 * (om * rm - CONE_C2 * b);
      float qc = om * om - CONE_C2 * rr;
      float qd = qb * qb - 4.0 * qa * qc;
      float inStart = t0;
      float inEnd = t1;
      if (abs(qa) < 1e-7) {
        float root = -qc / qb;
        if (qb > 0.0) inStart = max(t0, root); else inEnd = min(t1, root);
      } else if (qd <= 0.0) {
        if (qa < 0.0) discard;
      } else {
        float sq = sqrt(qd);
        float r1 = (-qb - sq) / (2.0 * qa);
        float r2 = (-qb + sq) / (2.0 * qa);
        float lo = min(r1, r2);
        float hi = max(r1, r2);
        if (qa < 0.0) {
          inStart = max(t0, lo);
          inEnd = min(t1, hi);
        } else {
          bool before = t0 < lo;
          bool after = t1 > hi;
          if (!before && !after) discard;
          inStart = before ? t0 : max(t0, hi);
          inEnd = after ? t1 : min(t1, lo);
        }
      }
      if (inEnd <= inStart) discard;
      t0 = inStart;
      t1 = inEnd;
    }

    // The march. A step reaches at most to within two ribbon widths of the
    // nearest ribbon at the rate the distance to it was last seen to change
    // plus the most the folds can add (so no crossing is stepped over), and
    // is never shorter than AURORA_NEAR_KM: the folds are smooth enough that
    // a step that long cannot hide a dip of a ribbon's depth, and each step
    // is integrated whole, so a ray running along a ribbon edge-on takes
    // long steps through it. A ray that runs out of steps takes the rest in
    // one, so nothing is cut off.
    float kmPerUnit = EARTH_KM / uR;
    float unitPerKm = 1.0 / kmPerUnit;
    AuPoint qa = auPoint(ro + rd * t0);
    float seed = qa.hemi > 0.0 ? 0.0 : 517.0;
    vec3 dA = auSheets(qa, seed);
    vec3 rate = vec3(FIELD_LIP - FOLD_SLOPE);
    float ta = t0;
    vec3 acc = vec3(0.0);
    int used = 0;
    for (int i = 0; i < AURORA_STEPS; i++) {
      used = i + 1;
      float fp = ta * uPixelAngle * kmPerUnit;
      float w2 = 2.0 * sqrt(SHEET_W * SHEET_W + 2.25 * fp * fp);
      vec3 reach = (abs(dA) - w2) / min(rate + FOLD_SLOPE, vec3(FIELD_LIP));
      float stepKm = clamp(min(reach.x, min(reach.y, reach.z)), max(AURORA_NEAR_KM, 1.5 * w2), MAX_STEP_KM);
      // Away from darkness only the diffuse band's coarse sampling matters.
      if (qa.night <= 0.0) stepKm = max(stepKm, 0.5 * MAX_STEP_KM);
      float tb = min(ta + stepKm * unitPerKm, t1);
      if (i == AURORA_STEPS - 1) tb = t1;
      AuPoint qb = auPoint(ro + rd * tb);
      vec3 dB = auSheets(qb, seed);
      acc += auStep(qa, qb, dA, dB, ro, rd, ta, tb, kmPerUnit, seed);
      float segKm = max((tb - ta) * kmPerUnit, 1e-3);
      rate = abs(dB - dA) / segKm;
      qa = qb;
      dA = dB;
      ta = tb;
      if (ta >= t1) break;
    }

  #ifdef AURORA_DEBUG
    // Steps used (red) and whether the ray ran out of them before its end (green).
    gl_FragColor = vec4(float(used) / float(AURORA_STEPS), used >= AURORA_STEPS ? 1.0 : 0.0, 0.0, 1.0);
    return;
  #endif
    acc *= uIntensity * gate;
    // A soft shoulder: the brightest folds seen edge-on stay well under
    // the lit Earth, hue kept.
    float lum = dot(acc, vec3(0.2126, 0.7152, 0.0722));
    acc *= AURORA_PEAK * (1.0 - exp(-lum / AURORA_PEAK)) / max(lum, 1e-5);
    if (max(acc.r, max(acc.g, acc.b)) < 1e-4) discard;
    // Additive blending scales by alpha.
    gl_FragColor = vec4(acc, 1.0);
  }
`;
